import type { LensCardData } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';

import { csvMoney, csvRate } from '../csvValues';

export const todaySydney = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(now);
export const addDays = (date: string, days: number) =>
  new Date(Date.parse(`${date}T00:00:00Z`) + days * 86400000).toISOString().slice(0, 10);
export const validDate = (date: string | null): date is string =>
  Boolean(
    date &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    Number.isFinite(Date.parse(date)) &&
    new Date(date).toISOString().slice(0, 10) === date,
  );
export const scalar = (value: unknown): unknown =>
  value && typeof value === 'object' && 'value' in value ? value.value : value;
export const numeric = (value: unknown): number | null => {
  const raw = scalar(value);
  return raw === null || raw === undefined || raw === '' || !Number.isFinite(Number(raw)) ? null : Number(raw);
};
export const label = (value: unknown): string =>
  scalar(value) === '__empty__'
    ? '(Blank)'
    : scalar(value) === '__null__' || scalar(value) === null || scalar(value) === undefined
      ? '(Null)'
      : String(scalar(value));
export const divide = (a: number | null, b: number | null) => (a === null || b === null || b === 0 ? null : a / b);
export const change = (a: number | null, b: number | null) => {
  const ratio = divide(a, b);
  return ratio === null ? null : ratio - 1;
};
export const cardRows = (cards: Array<LensCardData>, id: string): Array<Record<string, unknown>> =>
  cards.find((card) => card.cardId === id)?.rows ?? [];
export function readSelection(params: URLSearchParams, key: string, fallback: Array<string> = []): Array<string> {
  try {
    const value = JSON.parse(params.get(key) ?? 'null');
    return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : fallback;
  } catch {
    return fallback;
  }
}
export function csv(
  rows: Array<Record<string, unknown>>,
  columns: Array<{ key: string; label: string; percentage?: boolean; decimals?: number; money?: boolean }>,
  metadata: Record<string, unknown>,
): string {
  const cell = (value: unknown) => csvCell(scalar(value), 'Unknown');
  // Money to the cent; rates and per-session ratios to six places; counts as stored.
  const value = (row: Record<string, unknown>, column: (typeof columns)[number]) =>
    column.money
      ? csvMoney(scalar(row[column.key]))
      : column.percentage || column.decimals
        ? csvRate(scalar(row[column.key]))
        : row[column.key];
  return (
    '\uFEFF' +
    [
      ...Object.entries(metadata),
      columns.map((column) => column.label),
      ...rows.map((row) => columns.map((column) => value(row, column))),
    ]
      .map((row) => row.map(cell).join(','))
      .join('\r\n')
  );
}
export function downloadCsv(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}
export const formatNumber = (value: unknown, percentage = false, decimals = 0) => {
  const n = numeric(value);
  // A dash for a ratio with no denominator or a value the source does not have.
  return n === null
    ? '–'
    : new Intl.NumberFormat(
        'en-AU',
        percentage
          ? { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 2 }
          : { maximumFractionDigits: decimals },
      ).format(n);
};

/** Re-selecting the active source mark clears its action filter. */
export const toggleMarkSelection = (selection: Array<string>, value: string): Array<string> =>
  selection.length === 1 && selection[0] === value ? [] : [value];
